import { Group, Button, TextInput, PasswordInput, Select } from '@mantine/core';
import { useForm } from '@mantine/form';

import { useAppSelector, selectUserRoles } from '@/shared';
import { useGetShops } from '@/modules/shops';
import { userRoleTitles } from '@/shared/utils/helpers/common';
import { useGetPartners } from '../hooks/use-get-partners';

import type { TUser, TUpdateUserParams } from '../store/types';

const emailPattern = /^\S+@\S+$/;

type FormData = Omit<Partial<TUpdateUserParams>, 'partnerId'> & {
  partnerId: string | null;
};

type Props = {
  user: TUser;
  onSubmit: (params: TUpdateUserParams) => void;
  onClose: () => void;
};

export const EditUserForm = (props: Props) => {
  const { user, onSubmit, onClose } = props;
  const userRoles = useAppSelector(selectUserRoles);
  const { partners, isLoading: isPartnersLoading } = useGetPartners();
  const { data: partnerShops = [], isLoading: isPartnerShopsLoading } = useGetShops('partner');
  const partnerOptions = partners
    .filter((partner) => partner.id !== user.id)
    .map((partner) => {
      const partnerShop = partnerShops.find((shop) => shop.ownerId === partner.id);

      return {
        value: partner.id.toString(),
        label: partnerShop
          ? `Пользователь #${partner.id} — ${partner.name} (${partner.email}), магазин #${partnerShop.id} — ${partnerShop.name}`
          : `Пользователь #${partner.id} — ${partner.name} (${partner.email})`,
      };
    });

  const form = useForm<FormData>({
    mode: 'uncontrolled',
    initialValues: {
      password: undefined,
      email: user?.email,
      name: user?.name,
      role: user?.role,
      partnerId: user.partnerId?.toString() ?? null,
    },
    validate: {
      name: (value) => !value ? 'Введите имя пользователя' : null,
      role: (value) => !value ? 'Выберите роль пользователя' : null,
      email: (value) => {
        if (!value) return 'Введите Email';
        if (value && !emailPattern.test(value)) return 'Введите корректный Email';
        return null;
      },
    },
  });

  const handleSubmit = (values: FormData) => {
    const params: TUpdateUserParams = {
      userId: user.id,
      password: values.password,
      email: values.email,
      name: values.name,
      role: values.role,
      partnerId: values.partnerId ? Number(values.partnerId) : null,
    };

    onSubmit(params);
    onClose();
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <TextInput
        label="Имя пользователя"
        placeholder=""
        key={form.key('name')}
        {...form.getInputProps('name')}
      />
      <Select
        label="Роль"
        placeholder=""
        data={userRoles.map((userRole) => ({ value: userRole, label: userRoleTitles[userRole] ?? userRole }))}
        key={form.key('role')}
        {...form.getInputProps('role')}
      />
      <TextInput
        label="Email"
        placeholder=""
        key={form.key('email')}
        {...form.getInputProps('email')}
      />
      <PasswordInput
        label="Пароль"
        placeholder=""
        key={form.key('password')}
        {...form.getInputProps('password')}
      />
      <Select
        label="Кем привлечён"
        placeholder="Не выбран"
        clearable
        searchable
        disabled={isPartnersLoading || isPartnerShopsLoading}
        nothingFoundMessage="Партнёры не найдены"
        data={partnerOptions}
        key={form.key('partnerId')}
        {...form.getInputProps('partnerId')}
      />
      <Group justify="flex-end" mt="30">
        <Button variant="outline" onClick={() => onClose()}>
          Отменить
        </Button>
        <Button type="submit">
          Редактировать
        </Button>
      </Group>
    </form>
  );
};
